<template>
  <div>
    <a-form-item :label="$t('monitor.resource_template.preview_resource')" :label-col="labelCol" :wrapper-col="wrapperCol">
      <base-select
        :key="previewResourceKey"
        :value="previewResourceId"
        :resource="monitorMeta.resource"
        :version="monitorMeta.apiVersion || 'v1'"
        remote
        isDefaultSelect
        :params="resourceParams"
        :select-props="{ placeholder: $t('common.select'), allowClear: true }"
        @change="handlePreviewResourceChange" />
    </a-form-item>

    <div class="mb-4">
      <div class="d-flex align-items-center justify-content-between mb-3">
        <div class="font-weight-bold">{{ $t('monitor.resource_template.config') }}</div>
      </div>

      <div v-if="!metricBlocks.length" class="metric-block-empty">{{ $t('monitor.resource_template.no_panels') }}</div>

      <div v-for="(block, index) in metricBlocks" :key="block.uid" class="metric-item">
        <div class="metric-item__header">
          <div class="metric-item__title">
            <a class="metric-item__toggle" @click="toggleCollapse(block)">
              <a-icon :type="block.collapsed ? 'right' : 'down'" />
            </a>
            <a-icon class="metric-item__icon" type="line-chart" />
            <span v-if="!block.editingName" class="metric-item__name text-truncate" :title="block.label">{{ block.label || '-' }}</span>
            <a v-if="!block.editingName" class="metric-item__edit" :title="$t('common.edit')" @click="startEditName(block)">
              <a-icon type="edit" />
            </a>
            <a-input
              v-else
              :ref="`nameInput-${block.uid}`"
              v-model="block.nameDraft"
              size="small"
              class="metric-item__name-input"
              @pressEnter="commitName(block)"
              @blur="commitName(block)" />
          </div>

          <div class="metric-item__actions">
            <span class="metric-item__actions-label">{{ $t('monitor.monitor_metric') }}：</span>
            <a-form :form="block.metricForm.fc" class="metric-item__config">
              <metric
                :form="block.metricForm"
                :decorators="block.decorators"
                :res_type_measurements="filteredMeasurements"
                :res_types="filteredResTypes"
                :loading="metricLoading"
                :disabled="disabled"
                @metricChange="payload => handleMetricChange(block, payload)"
                @metricClear="() => handleMetricClear(block)" />
            </a-form>
            <a v-if="!disabled" class="metric-item__delete" @click="removeMetricBlock(index)">{{ $t('common.delete') }}</a>
          </div>
        </div>

        <div v-show="!block.collapsed" class="metric-item__body">
          <div v-if="!previewResourceId" class="metric-item__empty">{{ $t('monitor.resource_template.preview_tip') }}</div>
          <div v-else-if="!blockPreviews[block.uid]" class="metric-item__empty">{{ $t('monitor.resource_template.preview_metric_tip') }}</div>
          <dashboard-card
            v-else
            :key="blockPreviews[block.uid].__previewKey"
            :panel="blockPreviews[block.uid]"
            hideHeader
            useLocalPanels
            time="1h"
            timeGroup="1m"
            :extraParams="extraParams"
            :edit-chart="noop" />
        </div>
      </div>

      <a-button v-if="!disabled" icon="plus" class="mb-2" @click="addMetricBlock()">
        {{ $t('monitor.resource_template.add_metric') }}
      </a-button>
      <a-button class="ml-3" type="primary" icon="plus" :disabled="disabled" @click="openRecommend">
        {{ $t('monitor.resource_template.add_builtin_metric') }}
      </a-button>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import DashboardCard from '@Monitor/components/MonitorCard/DashboardCard'
import Metric from '@Monitor/sections/Metric'
import { getMetricUnit } from '@Monitor/utils/chartTypes'
import {
  MONITOR_TEMPLATE_META,
  MONITOR_TEMPLATE_VMINSTANCE,
  matchTemplateMeasurement,
  resolvePanelMonitorType,
} from '@Monitor/utils/monitorTemplate'
import { metric_zh } from '@Monitor/constants'
import { MonitorHelper } from '@/utils/monitor'
import { getRequestT, uuid } from '@/utils/utils'
import WindowsMixin from '@/mixins/windows'
import { panelKey } from './monitorTemplateCatalog'

export default {
  name: 'MonitorTemplate',
  components: {
    DashboardCard,
    Metric,
  },
  mixins: [WindowsMixin],
  props: {
    // 策略类型，决定监控模板对应的资源
    category: {
      type: String,
      default: MONITOR_TEMPLATE_VMINSTANCE,
    },
    // 编辑时回填的面板配置
    presentPolicies: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    labelCol: {
      type: Object,
      default: () => ({ span: 3 }),
    },
    wrapperCol: {
      type: Object,
      default: () => ({ span: 21 }),
    },
  },
  data () {
    return {
      metricBlocks: [],
      previewResourceId: '',
      previewResource: null,
      helper: new MonitorHelper(this.$Manager, this.$store.getters.scope),
      metricLoading: false,
      res_type_measurements: {},
      res_types: [],
    }
  },
  computed: {
    ...mapGetters(['scope']),
    // 不同策略类型（虚拟机/容器主机/容器）对应不同的监控资源
    monitorMeta () {
      return MONITOR_TEMPLATE_META[this.category] || MONITOR_TEMPLATE_META[MONITOR_TEMPLATE_VMINSTANCE]
    },
    extraParams () {
      return { scope: this.scope }
    },
    resourceParams () {
      return {
        scope: this.scope,
        details: true,
        limit: 20,
        ...(this.monitorMeta.resourceParams || {}),
      }
    },
    filteredResTypes () {
      const loaded = this.res_types || []
      // 指定了可选资源类型时，只保留模板支持的，避免出现宿主机等其它资源类型
      if (this.monitorMeta.res_types) {
        const allowed = this.monitorMeta.res_types
        const hit = allowed.filter(item => loaded.includes(item))
        return hit.length ? hit : allowed
      }
      // 未指定时按模板的测量项推断所属资源类型（如宿主机、容器主机、容器）
      const hit = loaded.filter(item => this.matchMeasurements(item).length)
      // 推断不出时不做限制，保持与监控查询一致，避免出现空列表
      const types = hit.length ? hit : loaded
      // 已保存/已选面板的资源类型要保留在选项里，否则编辑时第一列回填不出来
      const used = this.metricBlocks.map(block => block.res_type).filter(item => !!item)
      return [...new Set([...types, ...used])]
    },
    // 新建面板时的默认资源类型：虚拟机取固定类型，宿主机/容器主机/容器取推断出的类型
    defaultResType () {
      if (this.monitorMeta.res_types) return this.monitorMeta.res_type || 'guest'
      // 优先使用能匹配模板测量项的资源类型（如 rds / redis），避免落到 guest
      const hit = (this.res_types || []).filter(item => this.matchMeasurements(item).length)
      if (hit.length) return hit[0]
      if (this.monitorMeta.res_type) return this.monitorMeta.res_type
      const types = this.filteredResTypes || []
      return types.length ? types[0] : ''
    },
    filteredMeasurements () {
      const all = this.res_type_measurements || {}
      const ret = {}
      Object.keys(all).forEach(resType => {
        const hit = this.matchMeasurements(resType)
        // 该资源类型下没有匹配的测量项时不做过滤，避免第二列没有数据
        ret[resType] = hit.length ? hit : (all[resType] || [])
      })
      return ret
    },
    previewResourceKey () {
      return `preview-resource-${this.monitorMeta.resource}`
    },
    previewIdKey () {
      return this.monitorMeta.idKey || 'vm_id'
    },
    blockPreviews () {
      const map = {}
      if (!this.previewResourceId) return map
      // 需要资源详情（如容器的 pod_id、container_name）时，等详情返回后再预览
      if (this.monitorMeta.needResourceDetail && !this.previewResource) return map
      this.metricBlocks.forEach(block => {
        const panel = this.buildPreviewPanel(block)
        if (panel) map[block.uid] = panel
      })
      return map
    },
  },
  created () {
    const panels = (this.presentPolicies && this.presentPolicies.panels) || []
    if (panels.length) {
      panels.forEach(item => {
        this.addMetricBlock({
          label: item.label,
          // 老数据可能只有 agent_ 前缀，这里兼容回填为 agent 资源类型
          res_type: item.res_type || (resolvePanelMonitorType(item.fromItem) === 'agent' ? 'agent' : 'guest'),
          fromItem: item.fromItem,
          seleteItem: item.seleteItem,
          unit: item.unit,
          groupBy: item.groupBy,
          as: item.as,
          selectType: item.selectType,
          tags: item.tags,
          currentMetric: {
            metricKey: item.fromItem,
            mertric: item.seleteItem,
            mertricItem: { key: item.seleteItem, label: item.label, description: { unit: item.unit, display_name: item.label } },
            metricKeyItem: { measurement: item.fromItem, key: item.fromItem, measurement_display_name: item.name },
          },
        })
      })
    } else if (this.monitorMeta.res_types) {
      // 资源类型确定的模板（虚拟机）可以先添加面板，其余等测量项加载后按推断的类型添加
      this.addMetricBlock()
    }
    this.getMeasurement()
  },
  methods: {
    noop () {},
    // 按模板支持的测量项（名称/前缀）筛选某个资源类型的监控指标
    matchMeasurements (resType) {
      const measurements = this.res_type_measurements[resType] || []
      return measurements.filter(item => matchTemplateMeasurement(this.monitorMeta, item.measurement))
    },
    async getMeasurement () {
      try {
        this.metricLoading = true
        const params = { scope: this.scope, $t: getRequestT() }
        const { data: { res_type_measurements, res_types } } = await new this.$Manager('unifiedmonitors', 'v1').get({
          id: 'measurements',
          params,
        })
        this.res_type_measurements = res_type_measurements || {}
        this.res_types = res_types || []
        // 容器主机/容器的资源类型依赖测量项推断，加载完成后再创建默认面板
        if (!this.metricBlocks.length) this.addMetricBlock()
      } finally {
        this.metricLoading = false
      }
    },
    createMetricBlock (initial = {}) {
      const block = {
        uid: uuid(),
        label: initial.label || '',
        labelTouched: !!initial.label,
        res_type: initial.res_type || this.defaultResType || this.monitorMeta.res_type || 'guest',
        currentMetric: initial.currentMetric || null,
        groupBy: initial.groupBy || [],
        as: initial.as || '',
        selectType: initial.selectType || '',
        tags: initial.tags || null,
        collapsed: false,
        editingName: false,
        nameDraft: '',
      }
      block.decorators = {
        metric_res_type: [
          'metric_res_type',
          {
            initialValue: block.res_type,
            rules: [{ required: true, message: this.$t('common.select') }],
          },
        ],
        metric_key: [
          'metric_key',
          {
            initialValue: initial.fromItem,
            rules: [{ required: true, message: this.$t('common.select') }],
          },
        ],
        metric_value: [
          'metric_value',
          {
            initialValue: initial.seleteItem,
            rules: [{ required: true, message: this.$t('common.select') }],
          },
        ],
      }
      block.metricForm = {
        fc: this.$form.createForm(this, {
          onValuesChange: (_, values = {}) => this.handleBlockValuesChange(block, values),
        }),
      }
      return block
    },
    addMetricBlock (initial) {
      const block = this.createMetricBlock(initial)
      this.metricBlocks.push(block)
      return block
    },
    removeMetricBlock (index) {
      this.metricBlocks.splice(index, 1)
    },
    handleBlockValuesChange (block, values) {
      if (Object.prototype.hasOwnProperty.call(values, 'metric_res_type') && values.metric_res_type) {
        block.res_type = values.metric_res_type
      }
      if (Object.prototype.hasOwnProperty.call(values, 'metric_key') && values.metric_key) {
        block.labelTouched = false
        block.label = ''
      }
      if (Object.prototype.hasOwnProperty.call(values, 'metric_value') && !values.metric_value) {
        block.currentMetric = null
        if (!block.labelTouched) block.label = ''
      }
    },
    async handlePreviewResourceChange (id) {
      this.previewResourceId = id || ''
      this.previewResource = null
      if (!id || !this.monitorMeta.needResourceDetail) return
      try {
        const manager = new this.$Manager(this.monitorMeta.resource, this.monitorMeta.apiVersion || 'v1')
        const { data } = await manager.get({ id, params: { details: true } })
        this.previewResource = data
      } catch (e) {
        this.previewResource = null
      }
    },
    // 查询/预览所需的额外标签，如容器需要 pod_id、container_name
    getPreviewExtraTags (block) {
      const ret = []
      const fn = this.monitorMeta.extraTags
      if (typeof fn === 'function' && this.previewResource) {
        ret.push(...fn(this.previewResource))
      }
      // 指标自带的固定标签（如存储桶 request=get）
      const tags = (block && block.tags) || {}
      Object.keys(tags).forEach(key => {
        ret.push({ key, value: tags[key], operator: '=' })
      })
      return ret
    },
    toggleCollapse (block) {
      block.collapsed = !block.collapsed
    },
    startEditName (block) {
      block.nameDraft = block.label
      block.editingName = true
      this.$nextTick(() => {
        const ref = this.$refs[`nameInput-${block.uid}`]
        const input = Array.isArray(ref) ? ref[0] : ref
        input && input.focus()
      })
    },
    commitName (block) {
      if (!block.editingName) return
      const value = (block.nameDraft || '').trim()
      block.label = value || this.deriveMetricFullName(block.currentMetric)
      block.labelTouched = !!block.label
      block.editingName = false
    },
    handleMetricChange (block, payload) {
      if (!payload || !payload.mertric) return
      const prev = block.currentMetric || {}
      const metricItem = (payload.mertricItem && Object.keys(payload.mertricItem).length) ? payload.mertricItem : prev.mertricItem
      const keyItem = (payload.metricKeyItem && Object.keys(payload.metricKeyItem).length) ? payload.metricKeyItem : prev.metricKeyItem
      block.currentMetric = {
        metricKey: payload.metricKey,
        mertric: payload.mertric,
        mertricItem: metricItem,
        metricKeyItem: keyItem,
      }
      if (keyItem && keyItem.metric_res_type) {
        block.res_type = keyItem.metric_res_type
      }
      if (!block.labelTouched) {
        block.label = this.deriveMetricFullName(block.currentMetric, payload.mertric)
      }
    },
    handleMetricClear (block) {
      block.currentMetric = null
      if (!block.labelTouched) {
        block.label = ''
      }
    },
    deriveMetricLabel (metric, fallback = '') {
      const mertricItem = (metric && metric.mertricItem) || {}
      const displayName = mertricItem.description && mertricItem.description.display_name
      if (displayName && metric_zh[displayName]) return metric_zh[displayName]
      return mertricItem.label || fallback
    },
    // 监控名称统一展示为「CPU使用率(agent_cpu.usage_active)」格式
    buildMetricName (baseLabel, measurement, field) {
      const base = baseLabel || field
      if (!base) return ''
      if (!measurement || !field) return base
      return `${base}(${measurement}.${field})`
    },
    deriveMetricFullName (metric, fallback = '') {
      if (!metric) return ''
      return this.buildMetricName(
        this.deriveMetricLabel(metric, fallback),
        metric.metricKey,
        metric.mertric,
      )
    },
    deriveMetricCategory (metric, fallback) {
      const metricKeyItem = (metric && metric.metricKeyItem) || {}
      const displayName = metricKeyItem.measurement_display_name
      if (displayName && metric_zh[displayName]) return metric_zh[displayName]
      return fallback
    },
    resolveTransfer (unit) {
      if (!unit) return 1
      const u = String(unit).toLowerCase()
      if (u.includes('bps') || u === 'b' || u === 'byte' || u === 'bytes') return 1024
      return 1
    },
    // 指标自带的查询字段（分组、别名、聚合方式等），推荐/回填时保存在 block 上
    pickPanelQueryFields (block) {
      const ret = {}
      if (block.groupBy && block.groupBy.length) ret.groupBy = block.groupBy
      if (block.as) ret.as = block.as
      // RDS 部分指标使用 last 聚合（如连接数），需要保留；selectFunction 供通用查询方法使用
      if (block.selectType) {
        ret.selectType = block.selectType
        ret.selectFunction = block.selectType
      }
      if (block.tags) ret.tags = block.tags
      return ret
    },
    buildPanel (block) {
      const values = block.metricForm.fc.getFieldsValue(['metric_res_type', 'metric_key', 'metric_value'])
      if (!values.metric_key || !values.metric_value) return null
      const metric = block.currentMetric || {}
      const unit = getMetricUnit(metric.mertricItem) || '%'
      const label = (block.label || '').trim() || this.deriveMetricFullName(metric) || values.metric_value
      return {
        label,
        name: this.deriveMetricCategory(metric, values.metric_key),
        res_type: values.metric_res_type || block.res_type || 'guest',
        fromItem: values.metric_key,
        seleteItem: values.metric_value,
        unit,
        transfer: this.resolveTransfer(unit),
        metric: `${values.metric_key}.${values.metric_value}`,
        monitor_type: resolvePanelMonitorType(values.metric_key, values.metric_res_type),
        // 宿主机等指标需要按标签分组（如磁盘按 device/path/fstype），保持与平台默认面板一致
        ...this.pickPanelQueryFields(block),
      }
    },
    buildPreviewPanel (block) {
      const metric = block.currentMetric
      if (!this.previewResourceId || !metric || !metric.metricKey || !metric.mertric) return null
      const unit = getMetricUnit(metric.mertricItem) || '%'
      const label = (block.label || '').trim() || this.deriveMetricFullName(metric) || metric.mertric
      const constants = {
        label,
        name: this.deriveMetricCategory(metric, metric.metricKey),
        res_type: block.res_type || 'guest',
        fromItem: metric.metricKey,
        seleteItem: metric.mertric,
        unit,
        transfer: this.resolveTransfer(unit),
        metric: `${metric.metricKey}.${metric.mertric}`,
        ...this.pickPanelQueryFields(block),
      }
      return {
        panel_name: constants.label,
        constants,
        queryData: this.helper.genServerQueryData(this.previewResourceId, constants, '', '', this.previewIdKey, '', true, this.getPreviewExtraTags(block)),
        __previewKey: `${constants.metric}|${unit}|${this.previewResourceId}`,
      }
    },
    openRecommend () {
      this.createDialog('RecommendMonitorTemplateDialog', {
        category: this.category,
        onApply: row => this.applyRecommend(row),
      })
    },
    applyRecommend (row) {
      // 虚拟机区分基础/Agent 资源类型；宿主机/容器主机/容器的资源类型按 measurements 推断
      const resType = row.res_type || this.defaultResType || this.monitorMeta.res_type || 'guest'
      this.addMetricBlock({
        label: this.buildMetricName(row.label, row.fromItem, row.seleteItem),
        res_type: resType,
        fromItem: row.fromItem,
        seleteItem: row.seleteItem,
        groupBy: row.groupBy,
        as: row.as,
        selectType: row.selectType,
        // 部分指标需要固定标签查询（如存储桶的 request=get）
        tags: row.tags || row.tag,
        currentMetric: {
          metricKey: row.fromItem,
          mertric: row.seleteItem,
          mertricItem: {
            key: row.seleteItem,
            label: row.label,
            description: { unit: row.unit, display_name: row.label },
          },
          metricKeyItem: {
            measurement: row.fromItem,
            key: row.fromItem,
            measurement_display_name: row.name,
            label: row.name,
          },
        },
      })
      this.$message.success(this.$t('monitor.resource_template.metric_added'))
    },
    /**
     * 校验并生成模板面板，校验失败返回 null
     */
    async getPanels () {
      const panels = []
      const seen = {}
      for (const block of this.metricBlocks) {
        const panel = this.buildPanel(block)
        if (!panel) {
          this.$message.warning(this.$t('monitor.resource_template.metric_required'))
          return null
        }
        const key = panelKey(panel)
        if (seen[key]) {
          this.$message.warning(this.$t('monitor.resource_template.panel_exists'))
          return null
        }
        seen[key] = true
        panels.push(panel)
      }
      if (!panels.length) {
        this.$message.warning(this.$t('monitor.resource_template.no_panels'))
        return null
      }
      return panels
    },
  },
}
</script>

<style lang="less" scoped>
.metric-block-empty {
  padding: 32px;
  margin-bottom: 16px;
  text-align: center;
  color: rgba(0, 0, 0, 0.35);
  background: #fafafa;
  border: 1px dashed #e5e5e5;
  border-radius: 6px;
}
.metric-item {
  margin-bottom: 12px;
  background: #fff;
  border: 1px solid #f0f0f0;
  border-radius: 2px;
}
.metric-item__header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  padding: 8px 12px;
  background: #fafafa;
  border-bottom: 1px solid #f0f0f0;
}
.metric-item__title {
  display: flex;
  flex: 1 1 auto;
  align-items: center;
  min-width: 0;
  font-weight: 500;
}
.metric-item__toggle {
  flex: 0 0 auto;
  margin-right: 6px;
  color: rgba(0, 0, 0, 0.65);
}
.metric-item__icon {
  flex: 0 0 auto;
  margin-right: 6px;
  font-size: 14px;
  color: rgba(0, 0, 0, 0.65);
}
.metric-item__name {
  flex: 0 1 auto;
  min-width: 0;
}
.metric-item__edit {
  flex: 0 0 auto;
  margin-left: 6px;
  font-size: 13px;
}
.metric-item__name-input {
  flex: 0 1 320px;
  width: 320px;
  min-width: 160px;
  margin-left: 6px;
}
.metric-item__actions {
  display: flex;
  flex: 0 1 auto;
  align-items: center;
  justify-content: flex-end;
  min-width: 0;
  margin-left: auto;
  padding-left: 16px;
}
.metric-item__actions-label {
  flex: 0 0 auto;
  margin-right: 4px;
  color: rgba(0, 0, 0, 0.65);
}
.metric-item__config {
  flex: 0 1 620px;
  min-width: 0;
  ::v-deep .ant-form-item {
    margin-bottom: 0;
  }
}
.metric-item__delete {
  flex: 0 0 auto;
  margin-left: 12px;
  color: #f5222d;
}
.metric-item__body {
  padding: 4px 12px 12px;
  ::v-deep .monitor-overview-card {
    border: none;
  }
}
.metric-item__empty {
  padding: 48px 12px;
  text-align: center;
  color: rgba(0, 0, 0, 0.35);
}
</style>
